import { FirestoreService } from "../firebase/firestoreService";
import {
  FirestoreCollectionReference,
  FirestoreDocumentReference,
} from "../firebase/references";

/** Nested PDF extraction settings stored on the extraction_rules document. */
export interface PdfConfigSettings {
  start_page?: number;
  end_page?: number;
  limit?: number;
  model?: string;
  enable_batch_processing?: boolean;
  batch_size?: number;
  [key: string]: unknown;
}

/** Firestore extraction_rules/latest document shape (pdf_config is one nested field). */
export interface PdfConfigData {
  pdf_config?: PdfConfigSettings;
  [key: string]: unknown;
}

/** Page range fields that can be patched without rewriting pdf_config. */
export interface PdfPageConfigUpdate {
  startPage?: number;
  endPage?: number;
}

export class NarPdfConfigService {
  static async getPdfConfig(
    payerId: string,
    subcollection: string,
  ): Promise<PdfConfigData> {
    try {
      const documentPath = FirestoreDocumentReference.narPdfConfig(
        payerId,
        subcollection,
      );
      const data =
        await FirestoreService.getDocument<PdfConfigData>(documentPath);
      return data;
    } catch (error) {
      console.error("Error fetching PDF config:", error);
      throw error;
    }
  }

  static async updatePdfConfig(
    payerId: string,
    subcollection: string,
    data: Partial<PdfConfigData>,
  ): Promise<void> {
    try {
      const collectionPath =
        FirestoreCollectionReference.narPdfConfigExtractionRules(
          payerId,
          subcollection,
        );
      const docId = "latest";
      await FirestoreService.updateDocument(collectionPath, docId, data);
    } catch (error) {
      console.error("Error updating PDF config:", error);
      throw error;
    }
  }

  /**
   * Patches only start_page and/or end_page using Firestore field paths so other
   * pdf_config keys (limit, model, batch settings, etc.) are preserved.
   *
   * @param payerId - Auth grid payer identifier
   * @param subcollection - Plan/subcollection name
   * @param pageUpdate - Optional start and/or end page numbers to write
   */
  static async updatePdfPageConfig(
    payerId: string,
    subcollection: string,
    pageUpdate: PdfPageConfigUpdate,
  ): Promise<void> {
    const fieldsToUpdate: Record<string, number> = {};

    if (pageUpdate.startPage !== undefined) {
      fieldsToUpdate["pdf_config.start_page"] = pageUpdate.startPage;
    }
    if (pageUpdate.endPage !== undefined) {
      fieldsToUpdate["pdf_config.end_page"] = pageUpdate.endPage;
    }

    if (Object.keys(fieldsToUpdate).length === 0) {
      return;
    }

    try {
      const collectionPath =
        FirestoreCollectionReference.narPdfConfigExtractionRules(
          payerId,
          subcollection,
        );
      await FirestoreService.updateDocument(
        collectionPath,
        "latest",
        fieldsToUpdate,
      );
    } catch (error) {
      console.error("Error updating PDF page config:", error);
      throw error;
    }
  }
}
